import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { cn } from "../../../lib/cn";

/**
 * Phase 2 — shadcn-style Sheet (side drawer) built on Radix Dialog.
 * Replaces `.aud-drawer`, `.ld-drawer`, `.rs-drawer`, `.kb-drawer` which
 * currently share the 680px-wide / `#0a1528` / `#14213d` design language.
 *
 * Default `side="right"` width is 680px (the reference design). Scoping
 * drawer (Phase 4) can override via `className="sm:max-w-[720px]"`.
 * Uses the `slideInR` keyframe we added to tailwind.config.js.
 *
 * Usage:
 *   <Sheet open={open} onOpenChange={setOpen}>
 *     <SheetContent side="right">
 *       <SheetHeader>
 *         <SheetTitle>Audit details</SheetTitle>
 *         <SheetDescription>...</SheetDescription>
 *       </SheetHeader>
 *       {body}
 *     </SheetContent>
 *   </Sheet>
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;

export const SheetOverlay = forwardRef(function SheetOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[1000] bg-[rgba(3,8,22,0.62)] backdrop-blur-[3px]",
        "data-[state=open]:animate-fadeIn",
        className,
      )}
      {...props}
    />
  );
});

const sheetVariants = cva(
  [
    "fixed z-[1001] bg-rc-surface border-rc-border text-rc-text-primary shadow-rc-lg",
    "flex flex-col overflow-hidden",
    "data-[state=open]:animate-slideInR",
  ].join(" "),
  {
    variants: {
      side: {
        right:  "top-0 right-0 h-full border-l w-[92vw] sm:max-w-[680px]",
        left:   "top-0 left-0 h-full border-r w-[92vw] sm:max-w-[680px]",
        top:    "top-0 left-0 right-0 border-b max-h-[90vh]",
        bottom: "bottom-0 left-0 right-0 border-t max-h-[90vh]",
      },
    },
    defaultVariants: { side: "right" },
  },
);

export const SheetContent = forwardRef(function SheetContent(
  { side = "right", className, children, ...props },
  ref,
) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
});

export function SheetHeader({ className, children, right }) {
  return (
    <div className={cn(
      "shrink-0 flex items-start justify-between gap-3 px-6 py-5 border-b border-rc-border bg-rc-surface",
      className,
    )}>
      <div className="min-w-0 flex-1">{children}</div>
      {right}
    </div>
  );
}

export function SheetBody({ className, children }) {
  return (
    <div className={cn("flex-1 overflow-auto px-6 py-5", className)}>
      {children}
    </div>
  );
}

export function SheetFooter({ className, children }) {
  return (
    <div className={cn(
      "shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-rc-border bg-rc-surface",
      className,
    )}>
      {children}
    </div>
  );
}

export const SheetTitle = forwardRef(function SheetTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-[18px] font-extrabold text-rc-text-primary tracking-[-0.01em]", className)}
      {...props}
    />
  );
});

export const SheetDescription = forwardRef(function SheetDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-[12.5px] text-rc-text-secondary leading-[1.55] mt-0.5", className)}
      {...props}
    />
  );
});
